import React, { useId } from 'react';
import { Input } from '@/components/ui';
import GooglePlacesAddress from '../../shared/GooglePlacesAddress';
import { FieldLabel } from './StepShell';
import {
  getPostalCodeLabel,
  getPostalCodePlaceholder,
  getRegionLabel,
} from '../../../utils/addressRegions';
import type { AddressFieldValues } from '../../../business/onboarding/onboardingLocations';

interface AddressFieldsProps {
  value: AddressFieldValues;
  onChange: (next: AddressFieldValues) => void;
  placeholder?: string;
}

/**
 * Street/city/region/postal capture shared by every place the wizard asks for
 * an address, so the home address and the business location can't drift apart.
 */
export const AddressFields: React.FC<AddressFieldsProps> = ({
  value,
  onChange,
  placeholder = 'Start typing an address…',
}) => {
  const id = useId();
  const set = (patch: Partial<AddressFieldValues>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel htmlFor={`${id}-line1`} required>
          Street address
        </FieldLabel>
        <GooglePlacesAddress
          value={value.addressLine1}
          placeholder={placeholder}
          onChange={data =>
            set({
              addressLine1: data.address1,
              city: data.city,
              state: data.state,
              zipCode: data.zipCode,
              country: data.country || value.country,
            })
          }
          onInputChange={next => set({ addressLine1: next })}
          className="block h-10 w-full rounded-lg border border-ink-200 bg-ink-50 px-3 text-sm text-ink-900 placeholder:text-ink-400 transition-colors hover:border-ink-300 focus:outline-none focus-visible:border-accent-500 focus-visible:ring-2 focus-visible:ring-accent-500/40"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="col-span-2">
          <FieldLabel htmlFor={`${id}-city`} required>
            City
          </FieldLabel>
          <Input
            id={`${id}-city`}
            value={value.city}
            onChange={e => set({ city: e.target.value })}
            autoComplete="address-level2"
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${id}-state`} required>
            {getRegionLabel(value.country)}
          </FieldLabel>
          <Input
            id={`${id}-state`}
            value={value.state}
            onChange={e => set({ state: e.target.value })}
            autoComplete="address-level1"
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${id}-zip`} required>
            {getPostalCodeLabel(value.country)}
          </FieldLabel>
          <Input
            id={`${id}-zip`}
            value={value.zipCode}
            onChange={e => set({ zipCode: e.target.value })}
            placeholder={getPostalCodePlaceholder(value.country)}
            autoComplete="postal-code"
          />
        </div>
      </div>
    </div>
  );
};
